/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Descriptions, Skeleton, Table, Typography } from 'antd';

// Misc imports
import { useCurrentBreakpoint } from '../../utils/hooks/useCurrentBreakpoint';
import is from '../../utils/is';
import useTheme from '../../utils/hooks/useTheme';
import { formatNumberToThousandSeparator } from '../../utils/string/formatNumberToThousandSeparator';

/*
  * Example of usage:
  * isDescriptionTable: true (set to true if you want to render a description table),
  * isTableLoading: (Set it to corresponding loading state),
  * emptyLines: 0, (set it to the number of empty lines you want to show if isDescriptionTable is true),
  * descriptionTableFields: [], (set it to the fields you want to show in the description table)
  * Example for descriptionTableFields:
     [{
        label: 'Total LiveChat Requests',
        dataField: 'liveChatRequests',
        isPercentage: false,
        isToBeFixed: false,
        isToBeConvertedToSecondsOrMinutes: false,
      }
    ](
      label: label to be shown,
      dataField: field to be shown,
      isPercentage: if the value is a percentage,
      isToBeFixed: if the value is to be fixed,
      isToBeConvertedToSecondsOrMinutes: if the value is to be converted to seconds or minutes
    )
  *  tableData: [], (set it to the data you want to show in the table if isDescriptionTable is false)
  *  tableColumns: [], (set it to the columns you want to show in the table if isDescriptionTable is false)
  *         tableColumns can be set as you would set antD table columns
  * scroll: 165 (set it to the y scroll you want to show in the table if isDescriptionTable is false)
  * noScroll: true, (set it to true if you don't want to show scroll in the table if isDescriptionTable is false)
  *
  * Case 1: If you want to render a description table, set isDescriptionTable to true and pass the corresponding data to descriptionTableFields and emptyLines
  * Example of case 1:
    const tableConfig = {
      descriptionTableData: data,
      isDescriptionTable: true,
      isTableLoading: isLoading,
      emptyLines: 2,
      descriptionTableFields: [
      {
        label: 'Total LiveChats',
        dataField: 'liveChats',
        isPercentage: false,
        isToBeFixed: false
      }
      ]
    };
  * Case 2: If you want to render a table, set isDescriptionTable to false and pass the corresponding data to tableData and tableColumns
  * Example of case 2:
    const tableConfig = {
      tableData: data,
      isDescriptionTable: false,
      isTableLoading: isLoading,
      tableColumns: [
        {
          title: 'Question',
          dataIndex: 'name',
          width: '70%',
          align: 'left',
          ellipsis: true
        }, {
          title: 'Appearances',
          dataIndex: 'count',
          width: '30%',
          align: 'center',
          ellipsis: true
        }
      ]
    };
  * Case 3: If you want to render a table with scroll, set isDescriptionTable to false and pass the corresponding data to tableData, tableColumns and scroll
  * Example of case 3:
    const tableConfig = {
      tableData: data,
      isDescriptionTable: false,
      isTableLoading: isLoading,
      tableColumns: [
        {
          title: 'Question',
          dataIndex: 'name',
          width: '70%',
          align: 'left',
          ellipsis: true
        }, {
          title: 'Appearances',
          dataIndex: 'count',
          width: '30%',
          align: 'center',
          ellipsis: true
        }
      ],
      scroll: 165
    };
  * Case 4: If you want to render a table without scroll, set isDescriptionTable to false and pass the corresponding data to tableData, tableColumns and noScroll
  * Example of case 4:
    const tableConfig = {
      tableData: data,
      isDescriptionTable: false,
      isTableLoading: isLoading,
      tableColumns: [
        {
          title: 'Question',
          dataIndex: 'name',
          width: '70%',
          align: 'left',
          ellipsis: true
        }, {
          title: 'Appearances',
          dataIndex: 'count',
          width: '30%',
          align: 'center',
          ellipsis: true
        }
      ],
      noScroll: true
    };
/**
 *
 *
 * @param {*} {
 *     descriptionTableData = object,
 *     tableData = array,
 *     isDescriptionTable = boolean,
 *     isTableLoading = boolean,
 *     emptyLines = number,
 *     descriptionTableFields = array,
 *     tableColumns = array,
 *     scroll = number,
 *     noScroll = boolean
 *   }
 * @return {*}
*/
const AnalyticsTable = (
  {
    descriptionTableData = {},
    tableData = [],
    isDescriptionTable = false,
    isTableLoading = true,
    emptyLines = 0,
    descriptionTableFields = [],
    tableColumns = [],
    scroll = 165,
    noScroll = false,
    ...other
  }
) => {
  const currentBreakPoint = useCurrentBreakpoint();
  const { t } = useTranslation();
  const { paddingSm, paddingMd } = useTheme();

  const statsLabelWidth = () => {
    if (currentBreakPoint === 'xl') {
      return '60%';
    }
    if (currentBreakPoint === 'lg') {
      return '65%';
    }
    if (currentBreakPoint === 'md') {
      return '60%';
    }
    if (currentBreakPoint === 'sm') {
      return '50%';
    }
    if (currentBreakPoint === 'xs') {
      return '50%';
    }
  };

  const createSecOrMinutesString = (seconds) => {
    if (seconds < 60) {
      return `${seconds} ${t('sec')}`;
    }
    return `${parseFloat(seconds / 60).toFixed(1)} ${t('min')}`;
  };

  const renderDescriptionItems = () => {
    const descriptionItems = [];

    descriptionTableFields.forEach((field) => {
      const { label, dataField, isPercentage, isToBeFixed, isToBeConvertedToSecOrMinutes } = field;
      const value = descriptionTableData[dataField];

      if (value) {
        if (is.number(value)) {
          let formattedValue = formatNumberToThousandSeparator(value);
          const fixedValue = isToBeFixed ? value.toFixed(1) : value;
          const percentageValue = isPercentage ? `${fixedValue}%` : fixedValue;
          if (isToBeConvertedToSecOrMinutes) {
            formattedValue = createSecOrMinutesString(fixedValue);
          } else if (isPercentage) {
            formattedValue = percentageValue;
          } else if (isToBeFixed) {
            formattedValue = fixedValue;
          }

          descriptionItems.push(
            <Descriptions.Item
              label={label}
              key={label}
              labelStyle={{ padding: `${paddingSm} ${paddingMd}`, width: statsLabelWidth() }}
              contentStyle={{ padding: `${paddingSm} ${paddingMd}` }}
            >
              {
                isTableLoading ? (
                  <Skeleton.Button size="small" active />
                ) : (
                  <Typography.Text
                    ellipsis
                    title={formattedValue}
                  >
                    {formattedValue}
                  </Typography.Text>
                )
              }
            </Descriptions.Item>
          );
        } else {
          descriptionItems.push(
            <Descriptions.Item
              label={label}
              key={label}
              labelStyle={{ padding: `${paddingSm} ${paddingMd}`, width: statsLabelWidth() }}
              contentStyle={{ padding: `${paddingSm} ${paddingMd}` }}
            >
              {
                isTableLoading ? (
                  <Skeleton.Button size="small" active />
                ) : (
                  <Typography.Text
                    ellipsis
                    title={isNaN(value) ? '-' : value}
                  >
                    {isNaN(value) ? '-' : value}
                  </Typography.Text>
                )
              }
            </Descriptions.Item>
          );
        }
      } else {
        descriptionItems.push(
          <Descriptions.Item
            label={label}
            key={label}
            labelStyle={{ padding: `${paddingSm} ${paddingMd}`, width: statsLabelWidth() }}
            contentStyle={{ padding: `${paddingSm} ${paddingMd}` }}
          >
            {
              isTableLoading ? (
                <Skeleton.Button size="small" active />
              ) : (
                <Typography.Text
                  ellipsis
                  title={isToBeConvertedToSecOrMinutes ? `0 ${t('sec')}` : '-'}
                >
                  {isToBeConvertedToSecOrMinutes ? `0 ${t('sec')}` : '-'}
                </Typography.Text>
              )
            }
          </Descriptions.Item>
        );
      }
    });

    for (let i = 0; i < emptyLines; i++) {
      descriptionItems.push(<Descriptions.Item key={i} label=" " labelStyle={{ width: statsLabelWidth() }}><br /> </Descriptions.Item>);
    }

    return descriptionItems;
  };

  return (
    <Fragment>
      {
        isDescriptionTable ? (
          <Descriptions
            bordered
            column={1}
            contentStyle={{ padding: `${paddingSm} ${paddingMd}` }}
            labelStyle={{ padding: `${paddingSm} ${paddingMd}`, width: statsLabelWidth() }}
          >
            {renderDescriptionItems()}
          </Descriptions>
        ) : noScroll ? (
          <Table
            dataSource={tableData}
            columns={tableColumns}
            pagination={false}
            loading={isTableLoading}
            {...other}
          />
        ) : (
          <Table
            dataSource={tableData}
            columns={tableColumns}
            pagination={false}
            loading={isTableLoading}
            className={classNames('w-100', { 'scroll-table': tableData.length > 3 })}
            scroll={tableData.length > 3 ? { y: scroll } : false}
            {...other}
          />
        )
      }
    </Fragment>
  );
};

AnalyticsTable.propTypes = {
  tableData: PropTypes.array,
  isDescriptionTable: PropTypes.bool,
  isTableLoading: PropTypes.bool,
  emptyLines: PropTypes.number,
  descriptionTableFields: PropTypes.array,
  tableColumns: PropTypes.array,
  scroll: PropTypes.number,
  descriptionTableData: PropTypes.object,
  noScroll: PropTypes.bool
};

export default AnalyticsTable;
